import {
  FileSpreadsheet,
  Building2,
  Bot,
  Package,
  FileCheck,
  Zap,
  Inbox,
  UserCircle,
  Wrench,
  Vault,
  Target,
  BarChart2,
  Activity,
  Brain,
  Database,
  CalendarRange,
  GraduationCap,
  MapPin,
  Mail,
  Phone,
  DollarSign,
  SlidersHorizontal,
  Sparkles,
  ListChecks,
  Plug,
  ShieldCheck,
  Newspaper,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface SidebarItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

export interface SidebarGroupDef {
  label: string;
  items: SidebarItem[];
}

/**
 * Returns the primary navigation items shown above the Records section.
 * @param demandForecastingEnabled - Whether the forecast tab is visible
 * @param canViewOpportunities - Whether the user may view opportunities
 * @param canViewDemandForecasts - Whether the user holds demandforecast.view
 * @param canViewAnalytics - Whether the current user holds analytics.view
 * @param hasAgentPulseAccess - Whether the user holds the agentpulse.view permission
 * @returns Array of primary sidebar items
 */
export const getPrimaryItems = (
  demandForecastingEnabled: boolean,
  canViewOpportunities: boolean,
  canViewDemandForecasts: boolean,
  canViewAnalytics: boolean,
  hasAgentPulseAccess: boolean,
): SidebarItem[] => [
  { title: "Inbox", url: "/inbox", icon: Inbox },
  ...(canViewOpportunities
    ? [{ title: "Opportunities", url: "/opportunities", icon: Target }]
    : []),
  ...(canViewAnalytics
    ? [{ title: "Analytics", url: "/analytics", icon: BarChart2 }]
    : []),
  ...(demandForecastingEnabled && canViewDemandForecasts
    ? [{ title: "Forecast", url: "/forecast", icon: CalendarRange }]
    : []),
  ...(hasAgentPulseAccess
    ? [{ title: "Agent Pulse", url: "/agent-pulse", icon: Activity }]
    : []),
];

/**
 * Builds the Records section items based on org capabilities.
 * @param standardAgentEnabled - Whether the standard agent is enabled
 * @param canViewLocations - Whether the user holds the locations view permission
 * @param canViewCompanies - Whether the user holds a companies view permission
 * @param canViewShopRules - Whether the user may view the Shop Rules page
 * @param canViewPriceLists - Whether the user holds a price lists view permission
 * @param canViewDatasets - Whether the user may view the Dataset Explorer
 * @param canViewVault - Whether the user may view the Vault (holds `vault.view`)
 * @param canViewItems - Whether the user may view Items (holds `items.view`)
 * @param canViewOperations - Whether the current user holds operations.view
 * @param canViewPeople - Whether the user may view people
 * @param canViewQuotes - Whether the user may view quotes (own or all)
 * @returns Array of sidebar items for the Records group
 */
export const getRecordsItems = (
  standardAgentEnabled: boolean,
  canViewLocations: boolean,
  canViewCompanies: boolean,
  canViewShopRules: boolean,
  canViewPriceLists: boolean,
  canViewDatasets: boolean,
  canViewVault: boolean,
  canViewItems: boolean,
  canViewOperations: boolean,
  canViewPeople: boolean,
  canViewQuotes: boolean,
): SidebarItem[] => {
  const items: SidebarItem[] = [];

  if (canViewItems) {
    items.push({ title: "Items", url: "/items", icon: Package });
  }

  if (canViewOperations) {
    items.push({ title: "Operations", url: "/operations", icon: Wrench });
  }

  if (canViewQuotes) {
    items.push({ title: "Quotes", url: "/quotes", icon: FileSpreadsheet });
  }

  if (canViewPriceLists) {
    items.push({
      title: "Price Lists",
      url: "/price-lists",
      icon: FileSpreadsheet,
    });
  }

  if (canViewCompanies) {
    items.push({ title: "Companies", url: "/companies", icon: Building2 });
  }

  if (canViewPeople) {
    items.push({ title: "People", url: "/people", icon: UserCircle });
  }

  if (canViewLocations) {
    items.push({ title: "Locations", url: "/locations", icon: MapPin });
  }

  if (standardAgentEnabled && canViewVault) {
    items.push({ title: "Vault", url: "/vault", icon: Vault });
  }

  if (canViewShopRules) {
    items.push({ title: "Shop Rules", url: "/shop-rules", icon: Brain });
  }

  if (canViewDatasets) {
    items.push({
      title: "Dataset Explorer",
      url: "/dataset-explorer",
      icon: Database,
    });
  }

  return items;
};

/**
 * Builds grouped settings sidebar items based on permissions and feature flags.
 * @param showAiWorkers - Whether the AI Workers tab should be visible
 * @param showEmailIntegrations - Whether the Email Integrations tab should be visible
 * @param canEditSettings - Whether the user may edit settings, gating the Roles tab
 * @param customRolesEnabled - Whether the custom roles feature flag is on, gating the Roles tab
 * @param canEditReportSettings - Whether the user may edit settings (and the org has status reports), gating Report Settings
 * @param canEditOnboardingSettings - Whether the user may configure onboarding, gating the Onboarding tab
 * @returns Array of settings groups, each containing a label and items
 */
export const getSettingsGroups = (
  showAiWorkers: boolean,
  showEmailIntegrations: boolean,
  canEditSettings: boolean,
  customRolesEnabled: boolean,
  canEditReportSettings: boolean,
  canEditOnboardingSettings: boolean,
): SidebarGroupDef[] => {
  const integrationItems: SidebarItem[] = [
    { title: "ERP Integration", url: "/settings?tab=erp", icon: Plug },
    ...(showEmailIntegrations
      ? [
          {
            title: "Email Integrations",
            url: "/settings?tab=email-integrations",
            icon: Mail,
          },
        ]
      : []),
  ];

  if (showAiWorkers) {
    integrationItems.splice(1, 0, {
      title: "AI Workers",
      url: "/settings?tab=ai-workers",
      icon: Bot,
    });
  }

  const groups: SidebarGroupDef[] = [
    {
      label: "Account",
      items: [
        {
          title: "Personal Settings",
          url: "/settings?tab=personal",
          icon: UserCircle,
        },
      ],
    },
    { label: "Integrations", items: integrationItems },
    {
      label: "Organization",
      items: [
        {
          title: "Organization",
          url: "/settings?tab=organization",
          icon: Building2,
        },
        ...(canEditSettings && customRolesEnabled
          ? [
              {
                title: "Roles",
                url: "/settings?tab=roles",
                icon: ShieldCheck,
              },
            ]
          : []),
        ...(canEditReportSettings
          ? [
              {
                title: "Report Settings",
                url: "/settings?tab=report-settings",
                icon: Newspaper,
              },
            ]
          : []),
        {
          title: "Quote Settings",
          url: "/settings?tab=quote-settings",
          icon: FileSpreadsheet,
        },
        {
          title: "Opportunity Settings",
          url: "/settings?tab=opportunity-settings",
          icon: Target,
        },
        {
          title: "Phone Numbers",
          url: "/settings?tab=phone-numbers",
          icon: Phone,
        },
        {
          title: "Price Models",
          url: "/settings?tab=price-models",
          icon: DollarSign,
        },
        {
          title: "Item Settings",
          url: "/settings?tab=item-defaults",
          icon: ListChecks,
        },
        {
          title: "Company Settings",
          url: "/settings?tab=company-settings",
          icon: Building2,
        },
        ...(canEditOnboardingSettings
          ? [
              {
                title: "Onboarding",
                url: "/settings?tab=onboarding",
                icon: GraduationCap,
              },
            ]
          : []),
      ],
    },
  ];

  groups.push({
    label: "AI Configuration",
    items: [
      {
        title: "Agent Settings",
        url: "/settings?tab=agent-settings",
        icon: SlidersHorizontal,
      },
      {
        title: "Custom Skills",
        url: "/settings?tab=custom-skills",
        icon: Sparkles,
      },
    ],
  });

  return groups;
};

/**
 * Checks whether a sidebar link is active by comparing path and query params.
 * @param url - The link URL to check (may include query string)
 * @param pathname - The current route pathname
 * @param search - The current URL search string
 * @returns Whether the link should be highlighted as active
 */
export const isActiveLink = (url: string, pathname: string, search: string) => {
  const [path, query] = url.split("?");
  const isSamePath = pathname === path || pathname.startsWith(`${path}/`);

  if (!isSamePath) {
    return false;
  }

  if (!query) {
    return true;
  }

  const targetParams = new URLSearchParams(query);
  const currentParams = new URLSearchParams(search);

  for (const [key, value] of targetParams.entries()) {
    const currentValue = currentParams.get(key);
    // /settings with no tab param defaults to "personal"
    if (key === "tab" && currentValue === null && path === "/settings") {
      if (value !== "personal") {
        return false;
      }
      continue;
    }
    if (currentValue !== value) {
      return false;
    }
  }

  return true;
};

/**
 * Returns the appropriate icon for an agent based on its name.
 * @param agentName - The agent's display name
 * @returns A Lucide icon component
 */
export const getAgentIcon = (agentName: string): LucideIcon => {
  const nameLower = agentName.toLowerCase();
  if (nameLower.includes("quote") || nameLower.includes("pricing")) {
    return FileCheck;
  }
  if (nameLower.includes("workflow") || nameLower.includes("automation")) {
    return Zap;
  }
  if (nameLower.includes("test")) {
    return Package;
  }
  return Bot;
};

export { Bot } from "lucide-react";
